/**
 * Barre de progression : une métrique agrégée rapportée à un objectif.
 *
 * Pas d'ECharts — DOM + Tailwind. La logique de calcul est isolée dans
 * `computeProgress` (fonction pure, testée sans DOM), le composant ne fait
 * que la rendre.
 */
import type { WidgetConfig } from '../schemas';
import { useFieldCatalog, metricDef, metricLabel, metricFormat, type FieldCatalog } from '../fieldCatalog';
import { formatMetricValue } from '../formatValue';
import { aggregateMetric } from '../aggregate';
import { CHART_COLORS, COLOR_PRIMARY } from '../chartStyle';
import { resolveTarget } from './target';

type Row = Record<string, string | number>;

interface WidgetData {
  current: Row[];
  previous: Row[] | null;
  meta: any;
}

interface WidgetProps {
  config: WidgetConfig;
  data: WidgetData | null;
  loading?: boolean;
  error?: string;
}

// Cadre fourni par `WidgetShell` (WidgetRenderer) : ici, padding + hauteur seulement.
const SHELL = 'h-full';

export interface ProgressResult {
  /** Libellé FR de la métrique (jamais la clé brute). */
  label: string;
  /** Valeur agrégée (ratio-safe via `aggregateMetric`). */
  value: number;
  /** Objectif (`config.display.target`), `null` si absent ou invalide. */
  target: number | null;
  /** Avancement en %, borné à [0, 100] — toujours fini. */
  percent: number;
  /** L'objectif est dépassé (la barre reste à 100 %). */
  exceeded: boolean;
  valueLabel: string;
  targetLabel: string;
  /** `false` → état « À configurer » (métrique manquante, données absentes, objectif ≤ 0). */
  configured: boolean;
}

/**
 * Réduit la réponse de requête à un avancement vers l'objectif.
 *
 * Le pourcentage est borné à 100 (un dépassement d'objectif ne fait pas déborder la
 * barre, il est signalé par `exceeded`) et reste fini même sans objectif : sans cible
 * valide on renvoie 0 % et `configured: false`, jamais `Infinity`/`NaN`.
 */
export function computeProgress(
  config: WidgetConfig,
  data: WidgetData | null,
  catalog: FieldCatalog | null,
): ProgressResult {
  const metric = config.metrics?.[0];
  const target = resolveTarget(config.display?.target);
  const format = metric ? metricFormat(catalog, metric) : 'number';

  if (!metric || !data) {
    return {
      label: metric ? metricLabel(catalog, metric, config.metric_labels) : '',
      value: 0,
      target,
      percent: 0,
      exceeded: false,
      valueLabel: '—',
      targetLabel: target === null ? '—' : formatMetricValue(target, format),
      configured: false,
    };
  }

  const value = aggregateMetric(data.current, metric, metricDef(catalog, metric));

  const ratio = target === null ? 0 : (value / target) * 100;
  const percent = Math.max(0, Math.min(100, ratio));

  return {
    label: metricLabel(catalog, metric, config.metric_labels),
    value,
    target,
    percent,
    exceeded: target !== null && value > target,
    valueLabel: formatMetricValue(value, format),
    targetLabel: target === null ? '—' : formatMetricValue(target, format),
    configured: target !== null,
  };
}

/** Couleur de la barre : ambre loin de la cible, indigo en route, vert cible atteinte. */
function barColor(percent: number, exceeded: boolean): string {
  if (exceeded || percent >= 100) return CHART_COLORS[1];
  if (percent >= 50) return COLOR_PRIMARY;
  return CHART_COLORS[2];
}

export default function ProgressBarWidget({ config, data, loading, error }: WidgetProps) {
  // Hook avant tout early return (règle des hooks) : libellé FR + format + type de la métrique.
  const catalog = useFieldCatalog(data?.meta?.source_type as string | undefined);

  if (loading) {
    return (
      <div className={SHELL}>
        <div className="h-3 w-1/2 bg-[#F4F5FA] rounded animate-pulse mb-3" />
        <div className="h-2 w-full bg-[#F4F5FA] rounded animate-pulse" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`${SHELL} flex items-center`}>
        <span className="text-sm text-red-600">{error}</span>
      </div>
    );
  }

  const progress = computeProgress(config, data, catalog);

  if (!config.data_source_id || !progress.configured) {
    return (
      <div className={`${SHELL} flex items-center justify-center`}>
        <span className="text-sm text-[#8B8FA7]">À configurer</span>
      </div>
    );
  }

  // Le StyleTab peut forcer la couleur de la barre (`format.color`) ; sinon on
  // retombe sur la couleur calculée selon l'avancement.
  const color =
    typeof config.format?.color === 'string' && config.format.color
      ? config.format.color
      : barColor(progress.percent, progress.exceeded);

  return (
    <div className={`${SHELL} flex flex-col justify-center`}>
      <div className="text-[13px] text-[#8B8FA7] font-medium mb-2">
        {config.title || progress.label}
      </div>

      <div className="flex items-baseline gap-2 mb-2">
        <span className="text-2xl font-bold font-mono leading-none" style={{ color: '#1B2559' }}>
          {formatMetricValue(progress.percent / 100, 'percent', { decimals: 0 })}
        </span>
        <span className="text-sm text-[#8B8FA7]">
          {progress.valueLabel} / {progress.targetLabel}
        </span>
      </div>

      <div
        className="h-2 rounded-full bg-[#F4F5FA] overflow-hidden"
        role="progressbar"
        aria-valuenow={Math.round(progress.percent)}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className="h-2 rounded-full transition-all"
          style={{ width: `${progress.percent}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
}
